import { get, keys } from "idb-keyval";
import React, { useCallback, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { injectIntl } from "react-intl";
import { Redirect, Route, BrowserRouter as Router, useHistory, useLocation } from "react-router-dom";
import { Button } from "reactstrap";

import { AuthContext, AuthProvider } from "./Config/Auth";
import { ConnectionProvider } from "./Config/Connection";
import defaultSetupConfiguration from "./Config/defaultSetupConfiguration";
import { FixtureDetailsProvider } from "./Config/FixtureDetails";
import { InactivityTrackerProvider } from "./Config/InactivityTracker";
import { ChatProvider } from "./Components/ChatWidget/ChatProvider.jsx";

import ErrorBoundary from "./Components/Base/ErrorBoundary/ErrorBoundary";
import { getFixtureStore } from "./Components/Base/Factories/getFixtureStore";
import InactivityWarning from "./Components/Base/InactivityWarning/InactivityWarning";
import LanguageSelect from "./Components/Base/LanguageSelect/LanguageSelect";
import LockScoreBanner from "./Components/Base/LockScoreBanner/LockScoreBanner";
import Logout from "./Components/Base/Logout/Logout.jsx";
import MessagePopup from "./Components/Base/MessagePopup";
import Notification from "./Components/Base/Notification/Notification";
import OnlineCheck from "./Components/Base/OnlineCheck/OnlineCheck";
import OpenBetting from "./Components/Base/OpenBetting/OpenBetting";
import PageLoader from "./Components/Base/PageLoader/PageLoader";
import Logger from "./Components/Base/Utilities/Logger";
import { checkOnlineStatus } from "./Components/Base/Utilities/Online";
import ReportIssue from "./Components/Collect/ReportIssue/ReportIssue";
import ExternalFixture from "./Components/ExternalFixture/ExternalFixture";
import Fixtures from "./Components/Fixtures/Fixtures";
import Game from "./Components/Game/Game";
import GameDetails from "./Components/GameDetails/GameDetails";
import BasicLogin from "./Components/Login/Login.jsx";
import Menu from "./Components/Menu/Menu";
import TeamSetup from "./Components/TeamSetup/TeamSetup";
import { FixtureIdHandler } from "./Config/FixtureIdHandler";
import SessionInfo from "./Config/SessionInfo.jsx";

import "./App.scss";

const logger = Logger.getInstance();

const ProtectedRoute = ({ currentState, updateState, render, ...rest }) => {
  const location = useLocation();
  const { accessToken, loading, setRedirectUrl } = useContext(AuthContext);

  if (loading) {
    // TODO loading state
    return null;
  }

  const renderUnauthorizedAccessRedirect = () => {
    if (currentState.redirectFromLogout) {
      setRedirectUrl("/fixtures");
      updateState("redirectFromLogout", null);
    } else {
      setRedirectUrl(location.pathname);
    }
    return <Redirect to="/login" />;
  };

  const renderPathAndClearRedirectUrl = (cb) => {
    setRedirectUrl("");
    return cb();
  };

  return (
    <Route
      {...rest}
      render={() => {
        return accessToken ? renderPathAndClearRedirectUrl(render) : renderUnauthorizedAccessRedirect();
      }}
    />
  );
};

const FixtureLockedMessagePopup = injectIntl(({ intl, updateState }) => {
  const node = document.getElementById("modals-container");
  const { formatMessage } = intl;
  const history = useHistory();
  const text = formatMessage({
    id: "error.fixtureLocked",
    defaultMessage: "This fixture is locked, please contact your league administrator for assistance.",
  });
  const onConfirm = () => {
    updateState("show403Error", false);
    history.push("/fixtures");
  };
  return ReactDOM.createPortal(
    <div className="messagePopup--container">
      <div className="messagePopup--content">
        <div>{text}</div>
        <div className="messagePopup--buttons">
          <Button type="submit" color="success" onClick={onConfirm}>
            {formatMessage({
              id: "ok",
              defaultMessage: "OK",
            })}
          </Button>
        </div>
      </div>
    </div>,
    node,
  );
});

const App = (props) => {
  const { debug, locale, intl } = props;
  const { formatMessage } = intl;
  const setup = defaultSetupConfiguration;
  const [mainState, setMainState] = useState({
    notifications: [],
    deletedEventIds: [],
  });
  const [, updateAllState] = React.useState();
  const forceUpdate = useCallback(() => updateAllState({}), []);

  const [playStore, setPlayStore] = useState([]);
  const [eventStore, setEventStore] = useState([]);
  const [fixtureId, setFixtureId] = useState(null);
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const [logoutMessageType, setLogoutMessageType] = useState("default");

  function clearFixtureSpecificDateFromState() {
    setMainState({
      notifications: [],
      deletedEventIds: [],
      authKey: mainState.authKey,
      catchupsDone: false,
    });
  }

  function managePlays(key, data) {
    const getOldestEventFromPlay = (playId) => {
      const playEvents = eventStore.filter((ev) => ev.playId === playId);
      return playEvents.reduce((oldestEvent, currentEvent) => {
        if (
          new Date(currentEvent.eventTime.replace("Z", "")) <
          new Date((oldestEvent?.eventTime ?? new Date().toISOString()).replace("Z", ""))
        ) {
          return currentEvent;
        }
        return oldestEvent;
      }, null);
    };

    let plays = playStore;
    let playIndex = plays.findIndex((play) => play.playId === key);
    if (playIndex > -1) {
      // treat the oldest event in a play as the eventTime of the play
      plays[playIndex] = {
        ...plays[playIndex],
        ...data,
      };
      const oldestEvent = getOldestEventFromPlay(key);
      if (oldestEvent) plays[playIndex].eventTime = oldestEvent.eventTime;
    } else {
      plays.push(data);
    }
    setPlayStore(
      plays.sort((a, b) => (new Date(a.eventTime.replace("Z", "")) > new Date(b.eventTime.replace("Z", "")) ? -1 : 1)),
    );
    forceUpdate();
  }

  function manageEvents(key, data) {
    let events = eventStore;
    let eventIndex = events.findIndex((event) => event.eventId === key);
    if (eventIndex > -1) {
      if (data.status === "deleted") {
        events.splice(eventIndex, 1);
      } else {
        events[eventIndex] = data;
      }
    } else {
      if (data.status !== "deleted") {
        events.push(data);
      }
    }
    setEventStore(events);
    updateState("updateLog", key + Math.random());
    forceUpdate();
  }

  const updateState = useCallback(
    (key, data) => {
      if (key !== "updateLog" && key !== "mqtt") {
        logger.log("Updating state", {
          key,
          data: JSON.parse(JSON.stringify(data)),
        });
      }
      let thisState = mainState;
      thisState[key] = data;
      setMainState(thisState);
      forceUpdate();
    },
    [mainState, forceUpdate],
  );

  useEffect(() => {
    if (localStorage.getItem("authKey")) {
      updateState("authKey", localStorage.getItem("authKey"));
    }
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    setShowConfirmLogout(false);
    updateState("authKey", null);
    updateState("sessionInfo", null);
    localStorage.removeItem("authKey");
    updateState("redirectFromLogout", true);
  };

  async function handleOnlineCheckClick() {
    const wasOffline = !mainState.connected || !mainState.mqttConnected;
    const connectUrl = mainState.connectUrl;
    const online = await checkOnlineStatus(connectUrl);
    logger.log("Conn: Reconnect clicked", mainState.connected, mainState.mqttConnected, online);
    if (online && wasOffline) {
      updateState("forceReconnect", true);
    }
  }

  useEffect(() => {
    setPlayStore([]);
    setEventStore([]);
    if (fixtureId) {
      const eventDB = getFixtureStore(fixtureId, "event");
      const playDB = getFixtureStore(fixtureId, "play");
      keys(eventDB).then((eventKeys) => {
        eventKeys.map((eventId) => {
          get(eventId, eventDB).then((eventDetail) => {
            manageEvents(eventId, eventDetail);
          });
          return true;
        });
        keys(playDB).then((playKeys) => {
          playKeys.map((playId) => {
            get(playId, playDB).then((playDetail) => {
              managePlays(playId, playDetail);
            });
            return true;
          });
        });
        updateState("fixtureId", fixtureId);
        if (!mainState.events) {
          let events = [];
          events.main = {};
          updateState("events", events);
        }
        if (!mainState.queue) {
          let queue = [];
          updateState("queue", queue);
        }
      });
    }
    // eslint-disable-next-line
  }, [fixtureId]);

  useEffect(() => {
    logger.resetLogs();
  }, [fixtureId]);

  return (
    <Router>
      <ErrorBoundary currentState={mainState}>
        <InactivityTrackerProvider>
          <InactivityWarning fixtureId={fixtureId} />
          <FixtureIdHandler fixtureId={fixtureId} setFixtureId={setFixtureId} />
          <AuthProvider currentState={mainState} updateState={updateState}>
            <FixtureDetailsProvider
              key={mainState.fixtureId}
              fixtureId={mainState.fixtureId}
              currentState={mainState}
              updateState={updateState}
            >
              <ConnectionProvider
                key={mainState.fixtureId}
                fixtureId={mainState.fixtureId}
                currentState={mainState}
                updateState={updateState}
                manageEvents={manageEvents}
                managePlays={managePlays}
              >
                <ChatProvider currentState={mainState}>
                  <PageLoader currentState={mainState} />
                  <div className="App">
                    <div className="app-top-panel">
                      <Menu
                        orgnizationId={mainState.orgnizationId}
                        clock={mainState.clock}
                        locale={locale}
                        currentState={mainState}
                        formatMessage={formatMessage}
                      />
                      <div className="app-top-panel-right">
                        <LockScoreBanner currentState={mainState} updateState={updateState} />
                        {mainState.authKey && <OpenBetting currentState={mainState} eventStore={eventStore} />}
                        {mainState.authKey && (
                          <OnlineCheck
                            currentState={mainState}
                            updateState={updateState}
                            onClick={handleOnlineCheckClick}
                          />
                        )}
                        <SessionInfo currentState={mainState} updateState={updateState} />
                        <Notification
                          {...props}
                          currentState={mainState}
                          updateState={updateState}
                          manageEvents={manageEvents}
                          managePlays={managePlays}
                        />
                        <ReportIssue currentState={mainState} />
                        <LanguageSelect setup={setup} locale={locale} />
                        <Logout
                          mainState={mainState}
                          setShowConfirmLogout={setShowConfirmLogout}
                          setLogoutMessageType={setLogoutMessageType}
                        ></Logout>
                      </div>
                    </div>
                    <div id="modals-container"></div>
                    {mainState.show403Error && <FixtureLockedMessagePopup updateState={updateState} />}
                    {showConfirmLogout && (
                      <MessagePopup
                        text={formatMessage({
                          id:
                            logoutMessageType === "fixtureConfirm"
                              ? "logout.confirmationMessage.matchConfirm"
                              : "logout.confirmationMessage",
                          defaultMessage: "Are you sure you want to logout?",
                        })}
                        successButtonText={{ id: "ok", defaultMessage: "Ok" }}
                        onConfirm={logoutMessageType === "default" ? logout : () => setShowConfirmLogout(false)}
                        onDecline={logoutMessageType === "default" && (() => setShowConfirmLogout(false))}
                      />
                    )}
                    <React.Fragment>
                      <Route
                        exact
                        path="/"
                        render={() => (mainState.authKey ? <Redirect to="/fixtures" /> : <Redirect to="/login" />)}
                      />
                      <Route
                        exact
                        path={"/login"}
                        render={(props) => <BasicLogin {...props} updateState={updateState} currentState={mainState} />}
                      />
                      <ProtectedRoute
                        exact
                        path={"/fixtures"}
                        render={(props) => (
                          <Fixtures
                            {...props}
                            updateState={updateState}
                            clearFixtureSpecificDateFromState={clearFixtureSpecificDateFromState}
                          />
                        )}
                        currentState={mainState}
                        updateState={updateState}
                      />
                      <ProtectedRoute
                        exact
                        path={"/external-fixture"}
                        render={(props) => <ExternalFixture {...props} currentState={mainState} />}
                        currentState={mainState}
                        updateState={updateState}
                      />
                      <ProtectedRoute
                        exact
                        path={"/details/:fixtureId"}
                        render={(props) => (
                          <GameDetails {...props} updateState={updateState} currentState={mainState} />
                        )}
                        currentState={mainState}
                        updateState={updateState}
                      />
                      <ProtectedRoute
                        exact
                        path={"/setup/:fixtureId"}
                        render={(props) => <TeamSetup {...props} updateState={updateState} currentState={mainState} />}
                        currentState={mainState}
                        updateState={updateState}
                      />
                      <ProtectedRoute
                        exact
                        path={"/game/:fixtureId"}
                        render={(props) => (
                          <Game
                            {...props}
                            currentState={mainState}
                            updateState={updateState}
                            config={mainState.config}
                            updateLog={mainState.updateLog}
                            forceUpdate={forceUpdate}
                            debug={debug}
                            playStore={playStore}
                            eventStore={eventStore}
                            manageEvents={manageEvents}
                            managePlays={managePlays}
                          />
                        )}
                        currentState={mainState}
                        updateState={updateState}
                      />
                    </React.Fragment>
                  </div>
                </ChatProvider>
              </ConnectionProvider>
            </FixtureDetailsProvider>
          </AuthProvider>
        </InactivityTrackerProvider>
      </ErrorBoundary>
    </Router>
  );
};

export default injectIntl(App);
